/**
 * Faz 4-20: WebAuthn enrollment + management page.
 *
 * - Mevcut credentials'ları listele (Yenile, Sil)
 * - Yeni credential ekle (YubiKey, Touch ID, Windows Hello)
 * - Tarayıcı destek bilgisi (PublicKeyCredential + platform authenticator)
 */
import { appState } from '../state';
import { decodeCreateOptions, encodeAttestationResponse, detectWebAuthnSupport, } from '../services/webauthn';
import { applyStyle, escapeHtml } from '../ui/css';
import { createCard } from '../ui/components/card';
import { createButton } from '../ui/components/button';
import { createPageHeader } from '../ui/components/pageHeader';
import { icons } from '../ui/icons';
export function renderWebAuthn() {
    const root = document.createElement('div');
    root.append(createPageHeader({
        title: 'Donanım',
        highlight: 'anahtarları',
        subtitle: 'YubiKey, Windows Hello, Touch ID veya Face ID ile passwordless giriş.',
    }));
    // Destek satırı
    const supportLine = document.createElement('div');
    applyStyle(supportLine, {
        padding: 'var(--sp-3) var(--sp-4)',
        marginBottom: 'var(--sp-4)',
        background: 'var(--bg-elev-2)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 'var(--radius-md)',
        fontSize: 'var(--fs-caption)',
        color: 'var(--text-secondary)',
    });
    supportLine.textContent = 'Destek kontrol ediliyor...';
    root.append(supportLine);
    // Status banner
    const status = document.createElement('div');
    applyStyle(status, {
        padding: 'var(--sp-3) var(--sp-4)',
        marginBottom: 'var(--sp-4)',
        borderRadius: 'var(--radius-md)',
        display: 'none',
        fontSize: 'var(--fs-caption)',
        fontWeight: '500',
    });
    root.append(status);
    function showStatus(msg, kind) {
        status.textContent = msg;
        status.style.display = 'block';
        if (kind === 'ok') {
            status.style.background = 'rgba(34,197,94,0.1)';
            status.style.color = 'var(--status-online)';
            status.style.border = '1px solid rgba(34,197,94,0.4)';
        }
        else if (kind === 'err') {
            status.style.background = 'rgba(239,68,68,0.1)';
            status.style.color = 'var(--status-error)';
            status.style.border = '1px solid rgba(239,68,68,0.4)';
        }
        else {
            status.style.background = 'var(--accent-soft)';
            status.style.color = 'var(--accent)';
            status.style.border = '1px solid var(--accent)';
        }
    }
    // Add new
    const addCard = createCard({
        caption: 'YENİ ANAHTAR',
        title: 'Donanım anahtarı ekle',
        subtitle: 'Cihazın USB anahtarınla, parmak izi veya yüz tanıma ile bağlanır.',
    });
    const addBody = document.createElement('div');
    applyStyle(addBody, { display: 'flex', gap: 'var(--sp-2)', marginTop: 'var(--sp-3)', flexWrap: 'wrap' });
    const labelInput = document.createElement('input');
    labelInput.type = 'text';
    labelInput.placeholder = 'Cihaz etiketi (örn: İş YubiKey)';
    applyStyle(labelInput, {
        flex: '1',
        minWidth: '220px',
        padding: '10px 14px',
        fontSize: 'var(--fs-body)',
        background: 'var(--bg-elev-3)',
        border: '1px solid var(--border-strong)',
        borderRadius: 'var(--radius-sm)',
        color: 'var(--text-primary)',
    });
    const addBtn = createButton({
        label: 'Anahtar ekle',
        variant: 'primary',
        iconHtml: icons.key(),
    });
    addBody.append(labelInput, addBtn);
    addCard.append(addBody);
    applyStyle(addCard, { marginBottom: 'var(--sp-4)' });
    root.append(addCard);
    // List
    const listCard = createCard({
        caption: 'KAYITLI',
        title: 'Anahtarlar',
        headerRight: createButton({
            label: 'Yenile',
            variant: 'ghost',
            size: 'sm',
            iconHtml: icons.refresh(),
            onClick: () => loadList(),
        }),
    });
    const tableBox = document.createElement('div');
    applyStyle(tableBox, { marginTop: 'var(--sp-3)' });
    listCard.append(tableBox);
    root.append(listCard);
    // Initial check + load
    detectWebAuthnSupport().then((sup) => {
        if (!sup.supported) {
            supportLine.textContent =
                '⚠ Bu tarayıcı WebAuthn desteklemiyor. Modern Chrome/Edge/Firefox/Safari kullan.';
            supportLine.style.color = 'var(--status-error)';
            addBtn.disabled = true;
        }
        else {
            const platform = sup.platformAvailable
                ? '✓ Platform authenticator (Windows Hello / Touch ID) mevcut'
                : '⚠ Platform authenticator yok — yalnız harici USB anahtar kullanılabilir';
            supportLine.innerHTML = `<span style="color:var(--accent)">●</span> WebAuthn destekleniyor &nbsp; · &nbsp; ${escapeHtml(platform)}`;
        }
    });
    loadList();
    addBtn.addEventListener('click', async () => {
        const label = labelInput.value.trim() || 'FIDO2 key';
        addBtn.disabled = true;
        showStatus('Backend\'den challenge isteniyor...', 'info');
        try {
            const optsJson = await appState.backend.webauthnRegisterBegin();
            const opts = decodeCreateOptions(optsJson);
            showStatus('Authenticator\'ına dokun (PIN/biyometrik isteyebilir)...', 'info');
            const cred = (await navigator.credentials.create({ publicKey: opts }));
            if (!cred) {
                showStatus('Kullanıcı iptal etti veya cihaz cevap vermedi.', 'err');
                return;
            }
            showStatus('Backend\'e attestation gönderiliyor...', 'info');
            const result = await appState.backend.webauthnRegisterFinish(optsJson, encodeAttestationResponse(cred), label);
            showStatus(`✓ Kayıt başarılı: ${result.label} (id=${result.id.slice(0, 8)})`, 'ok');
            labelInput.value = '';
            await loadList();
        }
        catch (err) {
            showStatus(`✗ Hata: ${err.message ?? err}`, 'err');
        }
        finally {
            addBtn.disabled = false;
        }
    });
    async function loadList() {
        tableBox.innerHTML = '<em style="color:var(--text-tertiary);font-size:var(--fs-caption)">Yükleniyor...</em>';
        try {
            const rows = (await appState.backend.webauthnListCredentials());
            if (rows.length === 0) {
                tableBox.innerHTML = '';
                const empty = document.createElement('div');
                applyStyle(empty, {
                    padding: 'var(--sp-5)',
                    textAlign: 'center',
                    color: 'var(--text-tertiary)',
                    background: 'var(--bg-elev-3)',
                    border: '1px dashed var(--border-strong)',
                    borderRadius: 'var(--radius-sm)',
                    fontSize: 'var(--fs-caption)',
                });
                empty.textContent = 'Henüz kayıtlı anahtar yok. Yukarıdaki "Anahtar ekle" ile başla.';
                tableBox.append(empty);
                return;
            }
            tableBox.innerHTML = '';
            for (const row of rows) {
                const item = document.createElement('div');
                applyStyle(item, {
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--sp-3)',
                    padding: 'var(--sp-3) var(--sp-4)',
                    background: 'var(--bg-elev-3)',
                    border: '1px solid var(--border-subtle)',
                    borderRadius: 'var(--radius-sm)',
                    marginBottom: '8px',
                });
                const ic = document.createElement('div');
                ic.innerHTML = icons.key();
                applyStyle(ic, {
                    color: 'var(--accent)',
                    width: '36px',
                    height: '36px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'var(--accent-soft)',
                    borderRadius: 'var(--radius-sm)',
                });
                item.append(ic);
                const meta = document.createElement('div');
                applyStyle(meta, { flex: '1', display: 'flex', flexDirection: 'column', gap: '2px' });
                meta.innerHTML = `
          <strong style="color:var(--text-primary)">${escapeHtml(row.label)}</strong>
          <span style="color:var(--text-tertiary);font-size:var(--fs-tiny)">Eklendi: ${escapeHtml(new Date(row.createdAt).toLocaleString())}</span>
        `;
                item.append(meta);
                const delBtn = createButton({
                    label: 'Sil',
                    variant: 'subtle',
                    size: 'sm',
                    iconHtml: icons.trash(),
                    onClick: async () => {
                        if (!confirm(`"${row.label}" silinsin mi?`))
                            return;
                        try {
                            await appState.backend.webauthnDeleteCredential(row.id);
                            await loadList();
                            showStatus('Anahtar silindi.', 'ok');
                        }
                        catch (err) {
                            showStatus(`Silme hatası: ${err.message}`, 'err');
                        }
                    },
                });
                delBtn.style.color = 'var(--status-error)';
                item.append(delBtn);
                tableBox.append(item);
            }
        }
        catch (err) {
            tableBox.innerHTML = `<em style="color:var(--status-error);font-size:var(--fs-caption)">Yükleme hatası: ${escapeHtml(err.message ?? String(err))}</em>`;
        }
    }
    return root;
}
